import Icon from "@/components/ui/AppIcon";

interface QuickAccessCardsProps {
  currentLanguage: string;
  onChatOpen: () => void;
  onTicketOpen: () => void;
}

export default function QuickAccessCards({
  currentLanguage,
  onChatOpen,
  onTicketOpen,
}: QuickAccessCardsProps) {
  const content = {
    en: {
      title: "Quick Access",
      cards: [
        {
          icon: "ChatBubbleLeftRightIcon",
          title: "Live Chat Support",
          description:
            "Connect with our AI-powered chatbot or escalate to human agents instantly",
          action: "Start Chat",
          badge: "Available Now",
        },
        {
          icon: "TicketIcon",
          title: "Submit Support Ticket",
          description:
            "Create a detailed support request and track its progress in real-time",
          action: "Create Ticket",
          badge: "Response in 2 hours",
        },
        {
          icon: "PhoneIcon",
          title: "Emergency Hotline",
          description: "Critical issues? Call our 24/7 emergency support line",
          action: "+1-800-USS-HELP",
          badge: "24/7 Available",
        },
        {
          icon: "VideoCameraIcon",
          title: "Schedule Video Call",
          description:
            "Book a one-on-one video consultation with our technical experts",
          action: "Book Session",
          badge: "Next available: Today",
        },
      ],
    },
    hi: {
      title: "त्वरित पहुंच",
      cards: [
        {
          icon: "ChatBubbleLeftRightIcon",
          title: "लाइव चैट सहायता",
          description:
            "हमारे AI-संचालित चैटबॉट से जुड़ें या तुरंत मानव एजेंटों तक पहुंचें",
          action: "चैट शुरू करें",
          badge: "अभी उपलब्ध",
        },
        {
          icon: "TicketIcon",
          title: "सहायता टिकट सबमिट करें",
          description:
            "विस्तृत सहायता अनुरोध बनाएं और वास्तविक समय में इसकी प्रगति ट्रैक करें",
          action: "टिकट बनाएं",
          badge: "2 घंटे में प्रतिक्रिया",
        },
        {
          icon: "PhoneIcon",
          title: "आपातकालीन हॉटलाइन",
          description:
            "गंभीर समस्याएं? हमारी 24/7 आपातकालीन सहायता लाइन पर कॉल करें",
          action: "+1-800-USS-HELP",
          badge: "24/7 उपलब्ध",
        },
        {
          icon: "VideoCameraIcon",
          title: "वीडियो कॉल शेड्यूल करें",
          description:
            "हमारे तकनीकी विशेषज्ञों के साथ एक-पर-एक वीडियो परामर्श बुक करें",
          action: "सत्र बुक करें",
          badge: "अगला उपलब्ध: आज",
        },
      ],
    },
    ar: {
      title: "الوصول السريع",
      cards: [
        {
          icon: "ChatBubbleLeftRightIcon",
          title: "دعم الدردشة المباشرة",
          description:
            "اتصل بروبوت الدردشة المدعوم بالذكاء الاصطناعي أو قم بالتصعيد إلى وكلاء بشريين على الفور",
          action: "بدء الدردشة",
          badge: "متاح الآن",
        },
        {
          icon: "TicketIcon",
          title: "إرسال تذكرة الدعم",
          description: "إنشاء طلب دعم مفصل وتتبع تقدمه في الوقت الفعلي",
          action: "إنشاء تذكرة",
          badge: "الرد في ساعتين",
        },
        {
          icon: "PhoneIcon",
          title: "خط الطوارئ",
          description: "مشاكل حرجة؟ اتصل بخط الدعم الطارئ على مدار الساعة",
          action: "+1-800-USS-HELP",
          badge: "متاح 24/7",
        },
        {
          icon: "VideoCameraIcon",
          title: "جدولة مكالمة فيديو",
          description: "احجز استشارة فيديو فردية مع خبرائنا التقنيين",
          action: "حجز جلسة",
          badge: "التالي المتاح: اليوم",
        },
      ],
    },
  };

  const currentContent = content[currentLanguage as keyof typeof content];
  const isRTL = currentLanguage === "ar";

  const handleCardClick = (index: number) => {
    if (index === 0) {
      onChatOpen();
    } else if (index === 1) {
      onTicketOpen();
    }
  };

  return (
    <section data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:136:4" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="136" data-component-end-line="164" data-component-file="QuickAccessCards.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`py-16 bg-[#08111f] ${isRTL ? 'rtl' : 'ltr'}`}>
      <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:137:6" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="137" data-component-end-line="163" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
        <h2 data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:138:8" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="138" data-component-end-line="138" data-component-file="QuickAccessCards.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-white%20mb-8%20text-center%22%7D" className="text-3xl font-bold text-white mb-8 text-center">{currentContent.title}</h2>
        <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:139:8" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="139" data-component-end-line="162" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-5%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {currentContent.cards.map((card, index) => (
            <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:141:12" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="141" data-component-end-line="160" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20relative%20backdrop-blur-xl%20bg-%5B%231B365D%5D%2F20%20rounded-2xl%20p-6%20border%20border-white%2F10%20hover%3Aborder-%5B%230EA5E9%5D%2F40%20hover%3Ashadow-xl%20hover%3Ashadow-%5B%230EA5E9%5D%2F10%20transition-all%20duration-300%20cursor-pointer%22%7D"
              key={index}
              onClick={() => handleCardClick(index)}
              className="group relative backdrop-blur-xl bg-[#1B365D]/20 rounded-2xl p-6 border border-white/10 hover:border-[#0EA5E9]/40 hover:shadow-xl hover:shadow-[#0EA5E9]/10 transition-all duration-300 cursor-pointer"
            >
              <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:146:14" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="146" data-component-end-line="146" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20rounded-2xl%20bg-gradient-to-br%20from-%5B%230EA5E9%5D%2F5%20to-transparent%20opacity-0%20group-hover%3Aopacity-100%20transition-opacity%20pointer-events-none%22%7D" className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:147:14" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="147" data-component-end-line="154" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex%20justify-between%20items-start%20mb-4%22%7D" className="relative flex justify-between items-start mb-4">
                <div data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:148:16" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="148" data-component-end-line="150" data-component-file="QuickAccessCards.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20bg-gradient-to-br%20from-%5B%230EA5E9%5D%2F20%20to-%5B%231B365D%5D%2F30%20rounded-xl%20flex%20items-center%20justify-center%20border%20border-%5B%230EA5E9%5D%2F20%20group-hover%3Aborder-%5B%230EA5E9%5D%2F50%20transition-colors%22%7D" className="w-12 h-12 bg-gradient-to-br from-[#0EA5E9]/20 to-[#1B365D]/30 rounded-xl flex items-center justify-center border border-[#0EA5E9]/20 group-hover:border-[#0EA5E9]/50 transition-colors">
                  <Icon data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:149:18" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="149" data-component-end-line="149" data-component-file="QuickAccessCards.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-%5B%230EA5E9%5D%22%7D" name={card.icon as any} size={22} className="text-[#0EA5E9]" />
                </div>
                <span data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:151:16" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="151" data-component-end-line="153" data-component-file="QuickAccessCards.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-medium%20text-green-400%20bg-green-500%2F10%20border%20border-green-500%2F20%20px-2.5%20py-1%20rounded-full%22%7D" className="text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                  {card.badge}
                </span>
              </div>
              <h3 data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:155:14" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="155" data-component-end-line="155" data-component-file="QuickAccessCards.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22relative%20text-base%20font-semibold%20text-white%20mb-2%22%7D" className="relative text-base font-semibold text-white mb-2">{card.title}</h3>
              <p data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:156:14" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="156" data-component-end-line="156" data-component-file="QuickAccessCards.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22relative%20text-sm%20text-white%2F55%20mb-5%20leading-relaxed%22%7D" className="relative text-sm text-white/55 mb-5 leading-relaxed">{card.description}</p>
              <button data-component-id="src/app/(public)/global-support/components/QuickAccessCards.tsx:157:14" data-component-path="src/app/(public)/global-support/components/QuickAccessCards.tsx" data-component-line="157" data-component-end-line="159" data-component-file="QuickAccessCards.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22relative%20w-full%20py-2.5%20px-4%20rounded-xl%20font-semibold%20text-sm%20bg-gradient-to-r%20from-%5B%230EA5E9%5D%20to-%5B%231B365D%5D%20text-white%20shadow-lg%20shadow-%5B%230EA5E9%5D%2F20%20hover%3Ashadow-%5B%230EA5E9%5D%2F35%20transition-all%20duration-300%22%7D" className="relative w-full py-2.5 px-4 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#0EA5E9] to-[#1B365D] text-white shadow-lg shadow-[#0EA5E9]/20 hover:shadow-[#0EA5E9]/35 transition-all duration-300">
                {card.action}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
